/* RESPONSIBLE TEAM: team-app-security */
import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { taskFor } from 'ember-concurrency-ts';

import type IntlService from 'ember-intl/services/intl';
import type Store from '@ember-data/store';
import type Session from 'embercom/services/session';
import type ScimGroupService from 'embercom/services/scim-group-service';

import type {
  ScimGroup,
  Role,
  PendingChange,
  SeatType,
  CopilotSeat,
} from '../components/new-settings/workspace/teammates/scim/types';
import { SCIM_TRANSLATION_KEYS } from '../components/new-settings/workspace/teammates/scim/constants';
import { patchRequest } from 'embercom/lib/inbox/requests';
import { ScimGroupsState } from './scim-groups-state';
import { ScimGroupState } from '../components/new-settings/workspace/teammates/scim/types';

export default class ScimManagementService extends Service {
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare session: Session;
  @service declare scimGroupService: ScimGroupService;
  @service declare notificationsService: any;
  @service declare intercomEventService: any;
  @service declare appService: any;
  @service declare permissionsHierarchyService: any;

  @tracked currentState = new ScimGroupsState();
  @tracked changesState = new ScimGroupsState([], [], [], this.currentState);
  @tracked previewState = new ScimGroupsState([], [], [], this.currentState);

  @tracked isLoading = false;
  @tracked isLoadingUserGroups = false;
  @tracked isLoadingEditPermission = true;
  @tracked canEditScim = false;
  @tracked immuneAdminIds: string[] = [];

  get canSelectSeatType(): boolean {
    return this.appService.app?.canUseFeature?.('jit-seat-type-selection') ?? false;
  }

  @tracked showDetailsModal = false;
  @tracked showApplyChangesModal = false;
  @tracked showGroupDeactivationConfirmationModal = false;
  @tracked selectedGroup: ScimGroup | null = null;
  @tracked isLoadingGroupDetails = false;

  async loadData(availableRoles: Role[] = []): Promise<void> {
    await Promise.all([
      this.loadScimGroups(),
      this.loadCurrentAdminGroups(),
      this.loadEditPermission(),
    ]);

    this.currentState.availableRoles = availableRoles;

    this.changesState = new ScimGroupsState(
      this.currentState.groups,
      this.currentState.userGroups,
      availableRoles,
      this.currentState,
    );

    this.previewState = new ScimGroupsState(
      this.currentState.groups,
      this.currentState.userGroups,
      availableRoles,
      this.currentState,
    );
  }

  private async loadScimGroups(): Promise<void> {
    if (!this.session.workspace?.id) {
      this.currentState.groups = [];
      return;
    }

    this.isLoading = true;
    try {
      let scimGroups = await this.store.query('scim-group', {
        app_id: this.session.workspace.id,
        sort: 'priority',
      });

      let sortedGroups = scimGroups.slice().sort((a, b) => a.priority - b.priority);
      this.currentState.groups = sortedGroups;
      this.currentState.normalizePriorities();
      this.currentState.customGroupOrder = this.currentState.sortedGroups.map((g) => g.id);
    } catch (error) {
      console.error('Failed to load SCIM groups:', String(error));
      this.currentState.groups = [];
      throw error;
    } finally {
      this.isLoading = false;
    }
  }

  private async loadCurrentAdminGroups(): Promise<void> {
    if (!this.session.workspace?.id) {
      this.currentState.userGroups = [];
      return;
    }

    this.isLoadingUserGroups = true;
    try {
      let currentAdminId = this.appService.app.currentAdmin.id;
      let appId = this.session.workspace.id;

      let result = await taskFor(this.scimGroupService.fetchUserGroups).perform(
        appId,
        currentAdminId,
      );

      this.currentState.userGroups = result?.groups || [];
    } catch (error) {
      console.warn('Failed to load current admin groups:', String(error));
      this.currentState.userGroups = [];
    } finally {
      this.isLoadingUserGroups = false;
    }
  }

  private async loadEditPermission(): Promise<void> {
    if (!this.session.workspace?.id) {
      this.canEditScim = false;
      this.isLoadingEditPermission = false;
      return;
    }

    this.isLoadingEditPermission = true;
    try {
      let appId = this.session.workspace.id;
      let currentAdminId = this.appService.app.currentAdmin.id?.toString();
      if (!currentAdminId) {
        this.canEditScim = false;
        return;
      }

      let securitySettings: any = await this.store.findRecord('security-app-settings', appId);
      let scimSettings = securitySettings?.scimSettings;
      let immuneAdminIds: unknown = scimSettings?.immuneAdminIds;
      let ids: string[] = Array.isArray(immuneAdminIds)
        ? (immuneAdminIds as unknown[]).map((v) => String(v))
        : [];
      this.immuneAdminIds = ids;
      this.canEditScim = ids.includes(currentAdminId);
    } catch (e) {
      this.canEditScim = false;
    } finally {
      this.isLoadingEditPermission = false;
    }
  }

  getAdminCurrentRole(): { roleName: string; groupName: string; groupId: string } | null {
    return this.currentState.getUserEffectiveRole(this.intl);
  }

  getAdminNewRole(): { roleName: string; groupName: string; groupId: string } | null {
    return this.previewState.getUserEffectiveRole(this.intl);
  }

  getPendingChangesForReview(): PendingChange[] {
    let changes: PendingChange[] = [];

    let currentOrder = this.currentState.groups.map((g) => g.id);
    let hasOrderChanges = !this.changesState.arraysEqual(
      currentOrder,
      this.changesState.customGroupOrder,
    );

    if (hasOrderChanges) {
      changes.push({
        type: 'order' as const,
        id: 'order',
        title: this.intl.t(
          'new-settings.workspace.teammates.scim-tab.changes-review.order-change-title',
        ),
        oldValue: '',
        newValue: '',
        included: this.previewState.includeOrderChanges,
      });
    }

    for (let [groupIdStr, roleId] of Object.entries(this.changesState.roleChanges)) {
      let group = this.currentState.groups.find((g) => g.id === groupIdStr);
      if (group) {
        let oldRoleName = this.getRoleNameById(group.role_id);
        let newRoleName = this.getRoleNameById(roleId);

        changes.push({
          type: 'role' as const,
          id: groupIdStr,
          title: group.name,
          oldValue: oldRoleName,
          newValue: newRoleName,
          included: this.previewState.includedRoleChanges[groupIdStr] || false,
        });
      }
    }

    for (let [groupIdStr, seatType] of Object.entries(this.changesState.seatTypeChanges)) {
      let group = this.currentState.groups.find((g) => g.id === groupIdStr);
      if (group) {
        let oldSeatType = this.getSeatTypeDisplayName(group.seat_type as SeatType);
        let newSeatType = this.getSeatTypeDisplayName(seatType);

        changes.push({
          type: 'seat' as const,
          id: `seat-type-${groupIdStr}`,
          title: `${group.name} - ${this.intl.t('new-settings.workspace.teammates.scim-tab.seat-type.label')}`,
          oldValue: oldSeatType,
          newValue: newSeatType,
          included: this.previewState.includedSeatTypeChanges[groupIdStr] || false,
        });
      }
    }

    for (let [groupIdStr, copilotSeat] of Object.entries(this.changesState.copilotSeatChanges)) {
      let group = this.currentState.groups.find((g) => g.id === groupIdStr);
      if (group) {
        let oldCopilotSeat = this.getCopilotSeatDisplayName(group.copilot_seat as CopilotSeat);
        let newCopilotSeat = this.getCopilotSeatDisplayName(copilotSeat);

        changes.push({
          type: 'seat' as const,
          id: `copilot-seat-${groupIdStr}`,
          title: `${group.name} - ${this.intl.t('new-settings.workspace.teammates.scim-tab.copilot-seat.label')}`,
          oldValue: oldCopilotSeat,
          newValue: newCopilotSeat,
          included: this.previewState.includedCopilotSeatChanges[groupIdStr] || false,
        });
      }
    }

    return changes;
  }

  private getSeatTypeDisplayName(seatType: SeatType): string {
    switch (seatType) {
      case 'core':
        return this.intl.t('new-settings.workspace.teammates.scim-tab.seat-type.full');
      case 'light':
        return this.intl.t('new-settings.workspace.teammates.scim-tab.seat-type.lite');
      default:
        return this.intl.t('new-settings.workspace.teammates.scim-tab.seat-type.not-set');
    }
  }

  private getCopilotSeatDisplayName(copilotSeat: CopilotSeat): string {
    switch (copilotSeat) {
      case 'off':
        return this.intl.t('new-settings.workspace.teammates.scim-tab.copilot-seat.off');
      case 'included':
        return this.intl.t('new-settings.workspace.teammates.scim-tab.copilot-seat.included');
      case 'unlimited':
        return this.intl.t('new-settings.workspace.teammates.scim-tab.copilot-seat.unlimited');
      default:
        return this.intl.t('new-settings.workspace.teammates.scim-tab.copilot-seat.not-set');
    }
  }

  private getRoleNameById(roleId: number | null): string {
    let role = this.currentState.availableRoles.find((r) => r.id === roleId?.toString());
    return role?.name || this.intl.t(SCIM_TRANSLATION_KEYS.TAB.NO_ROLE_ASSIGNED);
  }

  @action
  toggleGroupChange(groupId: string): void {
    let isCurrentlyIncluded = this.previewState.includedRoleChanges[groupId];

    if (isCurrentlyIncluded) {
      let { [groupId]: _removed, ...remainingChanges } = this.previewState.roleChanges;
      this.previewState.roleChanges = remainingChanges;
      let { [groupId]: _removedIncluded, ...remainingIncluded } =
        this.previewState.includedRoleChanges;
      this.previewState.includedRoleChanges = remainingIncluded;
    } else if (this.changesState.roleChanges[groupId] !== undefined) {
      this.previewState.roleChanges[groupId] = this.changesState.roleChanges[groupId];
      this.previewState.includedRoleChanges[groupId] = true;
    }
  }

  @action
  toggleOrderChanges(): void {
    if (this.previewState.includeOrderChanges) {
      this.previewState.includeOrderChanges = false;
      this.previewState.customGroupOrder = [...this.currentState.groups.map((g) => g.id).sort()];
    } else {
      this.previewState.includeOrderChanges = true;
      this.previewState.customGroupOrder = [...this.changesState.customGroupOrder];
    }
  }

  @action
  toggleSeatChange(changeId: string): void {
    if (changeId.startsWith('seat-type-')) {
      let groupId = changeId.replace('seat-type-', '');
      let isCurrentlyIncluded = this.previewState.includedSeatTypeChanges[groupId];

      if (isCurrentlyIncluded) {
        let { [groupId]: _removed, ...remainingChanges } = this.previewState.seatTypeChanges;
        this.previewState.seatTypeChanges = remainingChanges;
        let { [groupId]: _removedIncluded, ...remainingIncluded } =
          this.previewState.includedSeatTypeChanges;
        this.previewState.includedSeatTypeChanges = remainingIncluded;
      } else if (this.changesState.seatTypeChanges[groupId] !== undefined) {
        this.previewState.seatTypeChanges[groupId] = this.changesState.seatTypeChanges[groupId];
        this.previewState.includedSeatTypeChanges[groupId] = true;
      }
    } else if (changeId.startsWith('copilot-seat-')) {
      let groupId = changeId.replace('copilot-seat-', '');
      let isCurrentlyIncluded = this.previewState.includedCopilotSeatChanges[groupId];

      if (isCurrentlyIncluded) {
        let { [groupId]: _removed, ...remainingChanges } = this.previewState.copilotSeatChanges;
        this.previewState.copilotSeatChanges = remainingChanges;
        let { [groupId]: _removedIncluded, ...remainingIncluded } =
          this.previewState.includedCopilotSeatChanges;
        this.previewState.includedCopilotSeatChanges = remainingIncluded;
      } else if (this.changesState.copilotSeatChanges[groupId] !== undefined) {
        this.previewState.copilotSeatChanges[groupId] =
          this.changesState.copilotSeatChanges[groupId];
        this.previewState.includedCopilotSeatChanges[groupId] = true;
      }
    }
  }

  hasIncludedChanges(): boolean {
    return this.previewState.hasIncludedChanges();
  }

  syncPreviewWithChanges(): void {
    this.previewState.resetToBaseState();

    let newRoleChanges: Record<string, number | null> = {};
    let newIncludedRoleChanges: Record<string, boolean> = {};
    Object.keys(this.changesState.roleChanges).forEach((groupId) => {
      if (this.changesState.includedRoleChanges[groupId]) {
        newRoleChanges[groupId] = this.changesState.roleChanges[groupId];
        newIncludedRoleChanges[groupId] = true;
      }
    });
    this.previewState.roleChanges = newRoleChanges;
    this.previewState.includedRoleChanges = newIncludedRoleChanges;

    let newSeatTypeChanges: Record<string, SeatType> = {};
    let newIncludedSeatTypeChanges: Record<string, boolean> = {};
    Object.keys(this.changesState.seatTypeChanges).forEach((groupId) => {
      if (this.changesState.includedSeatTypeChanges[groupId]) {
        newSeatTypeChanges[groupId] = this.changesState.seatTypeChanges[groupId];
        newIncludedSeatTypeChanges[groupId] = true;
      }
    });
    this.previewState.seatTypeChanges = newSeatTypeChanges;
    this.previewState.includedSeatTypeChanges = newIncludedSeatTypeChanges;

    let newCopilotSeatChanges: Record<string, CopilotSeat> = {};
    let newIncludedCopilotSeatChanges: Record<string, boolean> = {};
    Object.keys(this.changesState.copilotSeatChanges).forEach((groupId) => {
      if (this.changesState.includedCopilotSeatChanges[groupId]) {
        newCopilotSeatChanges[groupId] = this.changesState.copilotSeatChanges[groupId];
        newIncludedCopilotSeatChanges[groupId] = true;
      }
    });
    this.previewState.copilotSeatChanges = newCopilotSeatChanges;
    this.previewState.includedCopilotSeatChanges = newIncludedCopilotSeatChanges;

    if (this.changesState.includeOrderChanges) {
      this.previewState.customGroupOrder = [...this.changesState.customGroupOrder];
      this.previewState.includeOrderChanges = true;
    }
  }

  setupPreviewForSingleGroup(groupId: string): void {
    this.syncPreviewWithChanges();

    let targetChange = this.previewState.roleChanges[groupId];
    this.previewState.roleChanges = targetChange !== undefined ? { [groupId]: targetChange } : {};
    this.previewState.includedRoleChanges = targetChange !== undefined ? { [groupId]: true } : {};
    this.previewState.includeOrderChanges = false;
    this.previewState.customGroupOrder = [...this.currentState.groups.map((g) => g.id).sort()];
  }

  @action
  updateRole(groupId: string, roleId: number | null): void {
    if (this.appService.app.cannotAssignWiderAccess) {
      let targetRole: Role | null = null;
      if (roleId) {
        targetRole =
          this.currentState.availableRoles.find((r) => r.id === roleId.toString()) || null;
      }

      if (!this.canAssignRole(targetRole)) {
        console.warn('User attempted to assign role they do not have permission for:', {
          groupId,
          roleId,
        });
        this.notificationsService.notifyError(
          this.intl.t(
            'new-settings.workspace.teammates.scim-tab.tooltips.insufficient-permissions',
          ),
        );
        return;
      }
    }

    this.changesState.updateGroupRole(groupId, roleId);

    if (this.selectedGroup && this.selectedGroup.id === groupId) {
      this.setupPreviewForSingleGroup(groupId);
    }
  }

  @action
  reorderGroups(newOrder: ScimGroup[]): void {
    this.changesState.updateGroupOrder(newOrder.map((g) => g.id));
  }

  @action
  updateSeatType(groupId: string, seatType: SeatType): void {
    this.changesState.updateGroupSeatType(groupId, seatType);

    if (this.selectedGroup && this.selectedGroup.id === groupId) {
      this.setupPreviewForSingleGroup(groupId);
    }
  }

  @action
  updateCopilotSeat(groupId: string, copilotSeat: CopilotSeat): void {
    let currentSeatType =
      this.changesState.seatTypeChanges[groupId] ??
      this.currentState.groups.find((g) => g.id === groupId)?.seat_type;

    if (currentSeatType === 'light' && copilotSeat !== 'off' && copilotSeat !== null) {
      console.warn('Lite seats cannot have Copilot enabled');
      return;
    }

    this.changesState.updateGroupCopilotSeat(groupId, copilotSeat);

    if (this.selectedGroup && this.selectedGroup.id === groupId) {
      this.setupPreviewForSingleGroup(groupId);
    }
  }

  @action
  resetChanges(): void {
    this.changesState.resetToBaseState();
  }

  @action
  openGroupDetails(group: ScimGroup): void {
    this.selectedGroup = group;
    this.showDetailsModal = true;
    this.isLoadingGroupDetails = true;

    this.changesState.setupIncludedChanges();
    this.setupPreviewForSingleGroup(group.id);
  }

  @action
  closeGroupDetails(): void {
    this.showDetailsModal = false;
    this.selectedGroup = null;
    this.isLoadingGroupDetails = false;
  }

  @action
  openApplyChangesModal(): void {
    this.changesState.setupIncludedChanges();
    this.syncPreviewWithChanges();
    this.showApplyChangesModal = true;
  }

  @action
  closeApplyChangesModal(): void {
    this.showApplyChangesModal = false;
  }

  @action
  openGroupDeactivationConfirmation(): void {
    this.showGroupDeactivationConfirmationModal = true;
  }

  @action
  closeGroupDeactivationConfirmation(): void {
    this.showGroupDeactivationConfirmationModal = false;
  }

  async loadGroupDetails(groupId: number): Promise<ScimGroup> {
    let groupDetails = await this.store.findRecord('scim-group', groupId, {
      include: 'members',
      reload: true,
    });

    let groupIndex = this.currentState.groups.findIndex((g) => parseInt(g.id, 10) === groupId);
    if (groupIndex !== -1) {
      this.currentState.groups[groupIndex] = groupDetails;
    }

    if (this.selectedGroup && parseInt(this.selectedGroup.id, 10) === groupId) {
      this.selectedGroup = groupDetails;
    }

    this.isLoadingGroupDetails = false;
    return groupDetails;
  }

  canAssignRole(role: Role | null): boolean {
    if (!this.appService.app.cannotAssignWiderAccess || !role) {
      return true;
    }

    let adminPermissions = this.appService.app.currentAdmin.currentAppPermissions;
    if (!adminPermissions) {
      return false;
    }

    let rolePermissions = role.permissions();
    let permissionKeys = Object.keys(rolePermissions);

    return permissionKeys.every((permissionKey) => {
      let roleHasPermission = (rolePermissions as any)[permissionKey];
      let adminHasPermission = adminPermissions[permissionKey as keyof typeof adminPermissions];

      if (roleHasPermission && !adminHasPermission) {
        return false;
      }

      return true;
    });
  }

  canModifyGroupRole(groupId: string): boolean {
    if (!this.appService.app.cannotAssignWiderAccess) {
      return true;
    }

    let group = this.currentState.groups.find((g) => g.id === groupId);
    if (!group) {
      return false;
    }

    if (!group.role_id) {
      if (!this.currentState.availableRoles.some) {
        return true;
      }
      return (
        this.currentState.availableRoles.some((role) => this.canAssignRole(role)) ||
        this.canAssignRole(null)
      );
    }

    let currentRole = this.currentState.availableRoles.find(
      (r) => r.id === group.role_id?.toString(),
    );

    if (currentRole && !this.canAssignRole(currentRole)) {
      return false;
    }

    return true;
  }

  getUnassignableRolesInfo(): Record<string, { isDisabled: boolean; tooltipText: string }> {
    let unassignableRoles: Record<string, { isDisabled: boolean; tooltipText: string }> = {};

    if (!this.appService.app.cannotAssignWiderAccess) {
      return unassignableRoles;
    }

    this.currentState.availableRoles.forEach((role) => {
      if (!this.canAssignRole(role)) {
        unassignableRoles[role.id] = {
          isDisabled: true,
          tooltipText: this.intl.t(
            'new-settings.workspace.teammates.scim-tab.tooltips.insufficient-permissions',
          ),
        };
      }
    });

    return unassignableRoles;
  }

  async applyChanges(): Promise<void> {
    if (!this.previewState.hasIncludedChanges()) {
      return;
    }

    this.isLoading = true;

    let payload: {
      order?: number[];
      mapping?: Record<string, number | null>;
      states?: Record<string, string>;
      seat_type?: Record<string, SeatType>;
      copilot_seat?: Record<string, CopilotSeat>;
    } = {};

    let currentOrder = this.currentState.groups.map((g) => g.id);
    if (
      this.previewState.includeOrderChanges &&
      !this.previewState.arraysEqual(currentOrder, this.previewState.customGroupOrder)
    ) {
      payload.order = this.previewState.customGroupOrder.map((id) => parseInt(id, 10));
    }

    if (Object.keys(this.previewState.roleChanges).length > 0) {
      payload.mapping = this.previewState.roleChanges;

      let stateChanges: Record<string, string> = {};
      Object.entries(this.previewState.roleChanges).forEach(([groupId, roleId]) => {
        if (roleId !== null) {
          stateChanges[groupId] = ScimGroupState.ACTIVE;
        }
      });

      if (Object.keys(stateChanges).length > 0) {
        payload.states = stateChanges;
      }
    }

    let includedSeatTypeChanges: Record<string, SeatType> = {};
    Object.entries(this.previewState.seatTypeChanges).forEach(([groupId, seatType]) => {
      if (this.previewState.includedSeatTypeChanges[groupId]) {
        includedSeatTypeChanges[groupId] = seatType;
      }
    });
    if (Object.keys(includedSeatTypeChanges).length > 0) {
      payload.seat_type = includedSeatTypeChanges;
    }

    let includedCopilotSeatChanges: Record<string, CopilotSeat> = {};
    Object.entries(this.previewState.copilotSeatChanges).forEach(([groupId, copilotSeat]) => {
      if (this.previewState.includedCopilotSeatChanges[groupId]) {
        includedCopilotSeatChanges[groupId] = copilotSeat;
      }
    });
    if (Object.keys(includedCopilotSeatChanges).length > 0) {
      payload.copilot_seat = includedCopilotSeatChanges;
    }

    try {
      await patchRequest(`/ember/scim_group/?app_id=${this.session.workspace.id}`, payload);

      this.intercomEventService.trackAnalyticsEvent({
        action: 'updated',
        object: 'scim_groups_bulk',
        section: 'settings',
        place: 'workspace_teammates',
        applied_order_changes: Boolean(payload.order),
        applied_group_changes_count: Object.keys(this.previewState.roleChanges).length,
        applied_seat_type_changes_count: Object.keys(includedSeatTypeChanges).length,
        applied_copilot_seat_changes_count: Object.keys(includedCopilotSeatChanges).length,
      });

      Object.keys(this.previewState.roleChanges).forEach((groupIdStr) => {
        let { [groupIdStr]: _removed, ...remaining } = this.changesState.roleChanges;
        this.changesState.roleChanges = remaining;
        let { [groupIdStr]: _removedIncluded, ...remainingIncluded } =
          this.changesState.includedRoleChanges;
        this.changesState.includedRoleChanges = remainingIncluded;
      });

      await this.loadScimGroups();

      this.currentState.customGroupOrder = [];

      this.changesState.resetToBaseState();
      this.previewState.resetToBaseState();

      this.notificationsService.notifyConfirmation(
        this.intl.t(SCIM_TRANSLATION_KEYS.SUCCESS_MESSAGES.CHANGES_APPLIED),
        0,
      );
    } catch (error) {
      console.error('Failed to apply SCIM changes:', error);
      this.notificationsService.notifyError(
        this.intl.t(SCIM_TRANSLATION_KEYS.ERROR_MESSAGES.CHANGES_APPLIED),
      );
      throw error;
    } finally {
      this.isLoading = false;
    }
  }

  async deactivateGroup(groupId: string | number): Promise<void> {
    this.isLoading = true;

    try {
      let payload = {
        mapping: { [groupId]: null },
        states: { [groupId]: ScimGroupState.INACTIVE },
      };

      await patchRequest(`/ember/scim_group/?app_id=${this.session.workspace.id}`, payload);

      this.intercomEventService.trackAnalyticsEvent({
        action: 'updated',
        object: 'scim_group',
        section: 'settings',
        place: 'workspace_teammates',
        group_deactivated: true,
      });

      // Reload the groups to reflect the changes
      await this.loadScimGroups();

      // Clear any pending changes for this group
      let groupIdStr = groupId.toString();
      if (this.changesState.roleChanges[groupIdStr]) {
        let { [groupIdStr]: _removed, ...remaining } = this.changesState.roleChanges;
        this.changesState.roleChanges = remaining;
      }
      if (this.changesState.includedRoleChanges[groupIdStr]) {
        let { [groupIdStr]: _removedIncluded, ...remainingIncluded } =
          this.changesState.includedRoleChanges;
        this.changesState.includedRoleChanges = remainingIncluded;
      }

      // Reset states to reflect the new data
      this.changesState.resetToBaseState();
      this.previewState.resetToBaseState();

      this.notificationsService.notifyConfirmation(
        this.intl.t(SCIM_TRANSLATION_KEYS.SUCCESS_MESSAGES.GROUP_DEACTIVATED),
      );
    } catch (error) {
      console.error('Failed to deactivate SCIM group:', error);
      this.notificationsService.notifyError(
        this.intl.t(SCIM_TRANSLATION_KEYS.ERROR_MESSAGES.GROUP_DEACTIVATION),
      );
      throw error;
    } finally {
      this.isLoading = false;
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'scim-management-service': ScimManagementService;
  }
}
